'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowLeft, ArrowRight, Loader2, Plus, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Button } from '@/components/ui/button';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  containerTypeKeys,
  containerTypesApi
} from '@/features/container-types';
import { boxTypeKeys, boxTypesApi, type BoxType } from '@/features/box-types';
import { FieldTypeSidebar } from './field-type-sidebar';
import { FieldEditorCard } from './field-editor-card';
import type { LocalField, DynamicFieldType, DynamicFormType } from '../types';

interface StepAddFieldsProps {
  fields: LocalField[];
  onFieldsChange: (fields: LocalField[]) => void;
  /** Decides which type the form pins: a container type, or a box type. */
  formType: DynamicFormType | undefined;
  containerTypeId: number | '';
  onContainerTypeIdChange: (id: number | '') => void;
  boxTypeId: number | '';
  onBoxTypeIdChange: (id: number | '') => void;
  onNext: () => void;
  onPrevious: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

export function StepAddFields({
  fields,
  onFieldsChange,
  formType,
  containerTypeId,
  onContainerTypeIdChange,
  boxTypeId,
  onBoxTypeIdChange,
  onNext,
  onPrevious,
  onCancel,
  isSubmitting
}: StepAddFieldsProps) {
  const t = useTranslations('dynamicForms');

  // A packing form feeds the box form, which picks a box type — a container type
  // there was never read by anything downstream.
  const isPackingForm = formType === 'filling';

  const containerTypesSelect = useInfiniteSelect({
    queryKey: containerTypeKeys.lists(),
    queryFn: (params) => containerTypesApi.getAll(params),
    mapOption: useCallback(
      (ct: { id: number; name: string }) => ({
        value: String(ct.id),
        label: ct.name
      }),
      []
    ),
    params: { is_active: true }
  });

  const boxTypesSelect = useInfiniteSelect<BoxType>({
    queryKey: boxTypeKeys.lists(),
    queryFn: (params) => boxTypesApi.getAll(params),
    mapOption: useCallback(
      (bt: BoxType) => ({ value: String(bt.id), label: bt.name }),
      []
    ),
    params: { status: 'active' }
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  function handleAddField(type: DynamicFieldType) {
    const newField: LocalField = {
      tempId: crypto.randomUUID(),
      label: '',
      type,
      is_required: false,
      options: type === 'selection_list' ? ['', ''] : [],
      is_multi_select: false,
      default_value: '',
      sort_order: fields.length
    };
    onFieldsChange([...fields, newField]);
  }

  function handleUpdateField(tempId: string, updates: Partial<LocalField>) {
    onFieldsChange(
      fields.map((f) => (f.tempId === tempId ? { ...f, ...updates } : f))
    );
  }

  function handleRemoveField(tempId: string) {
    onFieldsChange(fields.filter((f) => f.tempId !== tempId));
  }

  function handleDuplicateField(tempId: string) {
    const source = fields.find((f) => f.tempId === tempId);
    if (!source) return;
    const duplicate: LocalField = {
      ...source,
      id: undefined,
      tempId: crypto.randomUUID(),
      label: `${source.label} (${t('copy')})`,
      sort_order: fields.length
    };
    const index = fields.findIndex((f) => f.tempId === tempId);
    const next = [...fields];
    next.splice(index + 1, 0, duplicate);
    onFieldsChange(next);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.tempId === active.id);
    const newIndex = fields.findIndex((f) => f.tempId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = [...fields];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    onFieldsChange(next);
  }

  return (
    <div>
      <div className='mb-6 flex flex-col items-center'>
        <div className='bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full'>
          <Plus className='text-primary h-8 w-8' />
        </div>
        <h3 className='mb-2 text-lg font-semibold'>{t('whatInfoToCollect')}</h3>
        <p className='text-muted-foreground text-sm'>{t('fieldTypeHint')}</p>
      </div>

      <div className='mx-auto mb-6 max-w-md'>
        <p className='mb-3 text-sm font-medium'>
          {isPackingForm ? t('boxType') : t('containerType')}
          <span className='ms-1 text-red-500'>*</span>
        </p>
        {isPackingForm ? (
          <InfiniteCombobox
            value={boxTypeId ? String(boxTypeId) : ''}
            onChange={(v) => onBoxTypeIdChange(v ? Number(v) : '')}
            placeholder={t('boxTypePlaceholder')}
            infiniteConfig={boxTypesSelect}
          />
        ) : (
          <InfiniteCombobox
            value={containerTypeId ? String(containerTypeId) : ''}
            onChange={(v) => onContainerTypeIdChange(v ? Number(v) : '')}
            placeholder={t('containerTypePlaceholder')}
            infiniteConfig={containerTypesSelect}
          />
        )}
        <p className='text-muted-foreground mt-1.5 text-xs'>
          {isPackingForm ? t('boxTypeHint') : t('containerTypeHint')}
        </p>
      </div>

      <div className='flex flex-col gap-6 sm:flex-row'>
        {/* Fields list */}
        <div className='flex-1'>
          <h4 className='mb-3 text-sm font-semibold'>
            {t('formFields')} ({fields.length})
          </h4>
          {fields.length === 0 ? (
            <div className='text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm'>
              {t('noFieldsAdded')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.tempId)}
                strategy={verticalListSortingStrategy}
              >
                <div className='space-y-3'>
                  {fields.map((field) => (
                    <FieldEditorCard
                      key={field.tempId}
                      field={field}
                      existingLabels={fields
                        .filter((f) => f.tempId !== field.tempId)
                        .map((f) => f.label)}
                      onUpdate={handleUpdateField}
                      onRemove={handleRemoveField}
                      onDuplicate={handleDuplicateField}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Type sidebar */}
        <div className='w-full shrink-0 sm:sticky sm:top-4 sm:w-64 sm:self-start'>
          <FieldTypeSidebar onAddField={handleAddField} />
        </div>
      </div>

      <div className='mt-8 flex items-center justify-between'>
        <Button type='button' variant='outline' onClick={onPrevious}>
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          {t('previous')}
        </Button>
        <div className='flex items-center gap-3'>
          {onCancel && (
            <Button type='button' variant='ghost' onClick={onCancel}>
              <X className='me-2 h-4 w-4' />
              {t('cancel')}
            </Button>
          )}
          <span className='text-muted-foreground text-sm'>
            {t('stepOf', { current: 2, total: 3 })}
          </span>
        </div>
        <Button type='button' onClick={onNext} disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className='me-2 h-4 w-4 animate-spin' />
          ) : null}
          {t('next')}
          <ArrowRight className='ms-2 h-4 w-4 rtl:rotate-180' />
        </Button>
      </div>
    </div>
  );
}
